import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Loader2, Target } from 'lucide-react';
import { rb, RBTextArea } from './theme';
import { resumeService } from '../../services/resumeService';

const MIN_JD_LENGTH = 50;

function ScoreRing({ label, value }) {
  const pct = Math.max(0, Math.min(100, Math.round(value)));
  const color = pct >= 70 ? '#19C37D' : pct >= 40 ? '#F59E0B' : '#EF4444';
  const r = 26;
  const circumference = 2 * Math.PI * r;
  return (
    <div className="flex flex-col items-center gap-1">
      <svg width="68" height="68" viewBox="0 0 68 68">
        <circle cx="34" cy="34" r={r} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="6" />
        <circle
          cx="34" cy="34" r={r} fill="none" stroke={color} strokeWidth="6" strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - pct / 100)}
          transform="rotate(-90 34 34)"
        />
        <text x="34" y="38" textAnchor="middle" fontSize="14" fontWeight="700" fill={color}>{pct}</text>
      </svg>
      <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</span>
    </div>
  );
}

// Paste a job description, get ATS match scores + keyword gaps for this resume.
export default function ATSPanel() {
  const [jd, setJd] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const analyze = async () => {
    if (jd.trim().length < MIN_JD_LENGTH || loading) return;
    setLoading(true);
    setError(null);
    try {
      const res = await resumeService.analyzeATS(jd.trim(), null);
      setResult(res);
    } catch (err) {
      setError(
        err?.response?.status === 429
          ? 'AI is busy right now — try again in a moment.'
          : "Couldn't run the ATS check. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const remaining = MIN_JD_LENGTH - jd.trim().length;

  return (
    <div className={`rounded-2xl p-4 sm:p-5 ${rb.glass}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className={`w-7 h-7 rounded-lg flex items-center justify-center ${rb.glowBtn}`}>
          <Target className="w-4 h-4 text-white" />
        </span>
        <h3 className="font-bold text-sm text-slate-100">ATS Check (optional)</h3>
      </div>
      <p className="text-xs mb-3 text-slate-400">
        Paste the job description you're applying for. We'll score your resume against it and show missing keywords.
      </p>
      <RBTextArea rows={4} value={jd} onChange={(e) => setJd(e.target.value)} placeholder="Paste the full job description here…" />
      <div className="flex items-center justify-between mt-2">
        <span className="text-[11px] text-slate-500">{remaining > 0 ? `${remaining} more characters needed` : ' '}</span>
        <button
          type="button"
          onClick={analyze}
          disabled={loading || jd.trim().length < MIN_JD_LENGTH}
          className={`inline-flex items-center gap-2 text-sm px-4 py-2 rounded-xl ${rb.glowBtn}`}
        >
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          {loading ? 'Analyzing…' : 'Check my resume'}
        </button>
      </div>

      {error && <p className="mt-3 text-xs text-red-400">{error}</p>}

      {result && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-4 space-y-4">
          <div className="flex flex-wrap justify-around gap-3">
            <ScoreRing label="Overall" value={result.overall_score} />
            <ScoreRing label="Keywords" value={result.keyword_match_score} />
            <ScoreRing label="Structure" value={result.structure_score} />
            <ScoreRing label="Content" value={result.content_score} />
          </div>

          {result.matched_keywords?.length > 0 && (
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wide mb-1.5 text-emerald-400">Found in your resume</p>
              <div className="flex flex-wrap gap-1.5">
                {result.matched_keywords.map((k) => (
                  <span key={k} className="px-2 py-0.5 rounded-full text-[11px] bg-emerald-500/15 text-emerald-300">{k}</span>
                ))}
              </div>
            </div>
          )}

          {result.missing_keywords?.length > 0 && (
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wide mb-1.5 text-red-400">Missing — try to add these</p>
              <div className="flex flex-wrap gap-1.5">
                {result.missing_keywords.map((k) => (
                  <span key={k} className="px-2 py-0.5 rounded-full text-[11px] bg-red-500/15 text-red-300">{k}</span>
                ))}
              </div>
            </div>
          )}

          {result.suggestions?.length > 0 && (
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wide mb-1.5 rb-grad-text">Suggestions</p>
              <ul className="list-disc pl-4 space-y-1 text-xs text-slate-200">
                {result.suggestions.map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
}
